import React, { useState, useEffect } from 'react';
import { getPerformanceTrends, getSubjectPerformance } from '../apiClient';

interface PerformanceOverviewProps {
  userStats: {
    tests_completed: number;
    overall_progress: number;
    study_time_hours: number;
    current_rank: number | string;
  };
}

interface SubjectData {
  name: string;
  score: number;
  color: string;
}

const PerformanceOverview: React.FC<PerformanceOverviewProps> = ({ userStats }) => {
  const [trends, setTrends] = useState<any>(null);
  const [subjects, setSubjects] = useState<SubjectData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [periodDays, setPeriodDays] = useState<number>(7);

  const subjectColorMap: { [key: string]: string } = {
    'Physics': '#667eea',
    'Chemistry': '#f39c12',
    'Mathematics': '#2d9cdb',
    'Math': '#2d9cdb',
    'Biology': '#27ae60',
    'English': '#e74c3c',
    'Computer Science': '#9b59b6',
    'History': '#34495e',
    'Geography': '#16a085'
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch performance trends
        const trendsResponse = await getPerformanceTrends(periodDays);
        if (trendsResponse && trendsResponse.result) {
          setTrends({
            accuracy: {
              value: trendsResponse.result.accuracy_trend.current,
              change: trendsResponse.result.accuracy_trend.change,
              trend: trendsResponse.result.accuracy_trend.trend
            },
            tests: {
              value: trendsResponse.result.tests_trend.current,
              change: trendsResponse.result.tests_trend.change,
              trend: trendsResponse.result.tests_trend.trend
            },
            rank: {
              value: trendsResponse.result.rank_trend.current,
              change: trendsResponse.result.rank_trend.change,
              trend: trendsResponse.result.rank_trend.trend
            },
            time: {
              value: trendsResponse.result.time_trend.current,
              change: trendsResponse.result.time_trend.change,
              trend: trendsResponse.result.time_trend.trend
            }
          });
        }

        // Fetch subject performance
        const subjectsResponse = await getSubjectPerformance();
        if (subjectsResponse && subjectsResponse.result && Array.isArray(subjectsResponse.result)) {
          const subjectsData = subjectsResponse.result.map((subject: any) => ({
            name: subject.subject_name || subject.subject,
            score: parseFloat(subject.accuracy) || 0,
            color: subjectColorMap[subject.subject_name || subject.subject] || '#6c757d'
          }));
          setSubjects(subjectsData);
        }
      } catch (error) {
        console.error('Failed to fetch performance data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodDays]);

  const getTrendIcon = (trend: string) => {
    return trend === 'up' ? '📈' : '📉';
  };

  const getTrendColor = (trend: string) => {
    return trend === 'up' ? '#27ae60' : '#e74c3c';
  };

  const formatChange = (change: number, isRank: boolean = false) => {
    const sign = change > 0 ? '+' : '';
    return isRank ? `${Math.abs(change)} positions` : `${sign}${change}`;
  };

  // Build stats array dynamically from trends data
  const stats = trends ? [
    {
      icon: 'fas fa-bullseye',
      label: 'Accuracy',
      value: `${trends.accuracy.value}%`,
      change: formatChange(trends.accuracy.change),
      trend: trends.accuracy.trend,
      color: '#667eea',
      bgColor: '#667eea15'
    },
    {
      icon: 'fas fa-clipboard-check',
      label: 'Tests Completed',
      value: trends.tests.value,
      change: formatChange(trends.tests.change),
      trend: trends.tests.trend,
      color: '#2d9cdb',
      bgColor: '#2d9cdb15'
    },
    {
      icon: 'fas fa-trophy',
      label: 'National Rank',
      value: `#${trends.rank.value}`,
      change: formatChange(trends.rank.change, true),
      trend: trends.rank.trend,
      color: '#f39c12',
      bgColor: '#f39c1215'
    },
    {
      icon: 'fas fa-clock',
      label: 'Study Time',
      value: `${trends.time.value}h`,
      change: formatChange(trends.time.change),
      trend: trends.time.trend,
      color: '#27ae60',
      bgColor: '#27ae6015'
    }
  ] : [];

  return (
    <div className="performance-overview-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div className="card-header bg-transparent border-0 p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="fw-bold mb-1">
                <i className="fas fa-chart-line me-2 text-primary"></i>
                Performance Overview
              </h4>
              <small className="text-muted">Last {periodDays} days performance metrics</small>
            </div>
            <div className="dropdown">
              <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" 
                type="button" 
                data-bs-toggle="dropdown">
                <i className="fas fa-calendar me-2"></i>
                Last {periodDays} Days
              </button>
              <ul className="dropdown-menu">
                <li><button className="dropdown-item" onClick={() => setPeriodDays(7)}>Last 7 Days</button></li>
                <li><button className="dropdown-item" onClick={() => setPeriodDays(30)}>Last 30 Days</button></li>
                <li><button className="dropdown-item" onClick={() => setPeriodDays(90)}>Last 90 Days</button></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="card-body p-4">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading performance data...</p>
            </div>
          )}

          {/* Stats Grid */}
          {!isLoading && stats.length > 0 && (
            <div className="row g-4 mb-4">
              {stats.map((stat, index) => (
              <div key={index} className="col-lg-3 col-md-6">
                <div className="p-4 rounded-4 h-100 position-relative overflow-hidden"
                  style={{ 
                    background: stat.bgColor,
                    border: `2px solid ${stat.color}20`,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = `0 10px 25px ${stat.color}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  
                  {/* Icon */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '50px',
                        height: '50px',
                        background: stat.color,
                        color: 'white'
                      }}>
                      <i className={stat.icon}></i>
                    </div>
                    <span style={{ fontSize: '24px' }}>{getTrendIcon(stat.trend)}</span>
                  </div>

                  {/* Value */}
                  <div className="h3 fw-bold mb-1" style={{ color: stat.color }}>
                    {stat.value}
                  </div>
                  
                  {/* Label */}
                  <div className="text-muted mb-2 small">{stat.label}</div>
                  
                  {/* Change */}
                  <div className="d-flex align-items-center">
                    <span className="badge rounded-pill px-3 py-2" 
                      style={{ 
                        background: getTrendColor(stat.trend) + '15',
                        color: getTrendColor(stat.trend),
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                      <i className={`fas fa-arrow-${stat.trend === 'up' ? 'up' : 'down'} me-1`}></i>
                      {stat.change}
                    </span>
                  </div>

                  {/* Mini chart simulation */}
                  <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '40px', opacity: 0.1 }}>
                    <svg width="100%" height="40" style={{ display: 'block' }}>
                      <polyline
                        fill="none"
                        stroke={stat.color}
                        strokeWidth="2"
                        points="0,30 25,20 50,25 75,15 100,10 125,20 150,15 175,10 200,5"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}

          {/* Subject Performance */}
          {!isLoading && subjects.length > 0 && (
            <div className="mt-4">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-book me-2 text-primary"></i>
              Subject-Wise Performance
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold">💪 Strongest</span>
                  <span className="badge bg-success px-3">
                    {subjects.reduce((prev, current) => prev.score > current.score ? prev : current).name}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold">📚 Needs Focus</span>
                  <span className="badge bg-warning px-3">
                    {subjects.reduce((prev, current) => prev.score < current.score ? prev : current).name}
                  </span>
                </div>
              </div>
            </div>

            {/* Subject bars */}
            <div className="mt-3">
              {subjects.map((subject, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="small fw-semibold">{subject.name}</span>
                    <span className="small fw-bold" style={{ color: subject.color }}>{subject.score}%</span>
                  </div>
                  <div className="progress" style={{ height: '8px', background: '#e9ecef' }}>
                    <div 
                      className="progress-bar" 
                      style={{ 
                        width: `${subject.score}%`,
                        background: subject.color,
                        transition: 'width 1s ease'
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceOverview;
